import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  toggleStarSpace,
  fetchRecentlyViewedSpaces,
} from '@core/spaceSelector/operations';
import {
  getHomeStarredSpaces,
  getHomeSpacesForView,
} from '@app/selectors/home';
import { getHomeRecentSpaces } from '../components/presentational/Home/widgets/recentlyViewedSpacesWidget/getHomeRecentSpaces';

const PAGE_SIZE = 20;

export const useAllSpaces = (
  filterType: 'all' | 'starred' | 'recent' = 'all'
) => {
  const dispatch = useDispatch<any>();

  const homeStarredSpaces = useSelector(getHomeStarredSpaces);
  const homeSpacesForView = useSelector(getHomeSpacesForView);
  const homeRecentSpaces = useSelector(getHomeRecentSpaces);

  // Convert map to sorted array like the dropdown does, and filter out archived spaces
  const realSpaces = useMemo(() => {
    return homeSpacesForView || [];
  }, [homeSpacesForView]);

  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch recently viewed spaces if not already fetched
  useEffect(() => {
    if (filterType === 'recent') {
      dispatch(fetchRecentlyViewedSpaces() as any);
    }
  }, [dispatch, filterType]);

  // We simulate a small delay for loading more to show the skeleton smoothly if needed
  // In a real API pagination, this would be an actual fetch request.
  const onLoadMore = useCallback(() => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);

    // Simulate network request for progressive rendering
    setTimeout(() => {
      setPage((p) => p + 1);
      setIsLoadingMore(false);
    }, 400);
  }, [isLoadingMore]);

  // Generate base list of spaces based on filterType
  const baseSpaces = useMemo(() => {
    if (filterType === 'starred') {
      return homeStarredSpaces || [];
    }

    if (filterType === 'recent') {
      return homeRecentSpaces || [];
    }

    return realSpaces || [];
  }, [realSpaces, filterType, homeStarredSpaces, homeRecentSpaces]);

  const spaces = useMemo(() => {
    return baseSpaces.slice(0, page * PAGE_SIZE).map((s: any) => ({
      id: s.objectId || s.id,
      name: s.name,
      startDate: s.startDate,
      endDate: s.endDate,
      lastNavigation: s.lastNavigation,
      avatar: s.avatar || null,
    }));
  }, [baseSpaces, page]);

  const hasMore = baseSpaces.length > page * PAGE_SIZE;

  const starredIds = useMemo(() => {
    const set = new Set<string>();
    (realSpaces || []).forEach((s: any) => {
      if (s.isStarred) {
        set.add(s.objectId || s.id);
      }
    });
    return set;
  }, [realSpaces]);

  const handleStarToggle = useCallback(
    (spaceId: string) => {
      dispatch(toggleStarSpace({ spaceId }));
    },
    [dispatch]
  );

  // Derive initial loading state from global data fetch to prevent premature empty-state evaluation on hard reloads
  const isLoading = useSelector((state: any) => !state.data?.hasInitialData);

  return {
    spaces,
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore,
    starredIds,
    handleStarToggle,
  };
};

export default useAllSpaces;
